import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { InvoiceFormDialog, INVOICE_STATUS, INVOICE_STYLES } from "@/pages/Business";
import { ArrowLeft, Printer, FileDown, Link2, Sparkles, Send, CheckCircle2, Ban, Trash2, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const InvoicePreview = ({ invoice, issuerName }) => (
  <div data-testid="invoice-preview" className="bg-white rounded-xl border border-slate-200 p-8 print:border-0 print:shadow-none">
    <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b border-slate-100">
      <div>
        <p className="text-lg font-extrabold text-brand-navy">NEXA — Facture</p>
        <p data-testid="invoice-number" className="text-sm text-slate-500 mt-1">{invoice.number}</p>
      </div>
      <span data-testid="invoice-status-badge" className={`text-xs font-semibold rounded-full px-3 py-1.5 ${INVOICE_STYLES[invoice.status]}`}>
        {INVOICE_STATUS[invoice.status]}
      </span>
    </div>
    <div className="grid sm:grid-cols-2 gap-6 py-6 text-sm">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Émetteur</p>
        <p className="font-medium text-brand-navy">{issuerName}</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Facturé à</p>
        <p data-testid="invoice-client" className="font-medium text-brand-navy">{invoice.client_name}</p>
        {invoice.client_email && <p className="text-slate-500">{invoice.client_email}</p>}
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Émission</p>
        <p className="text-slate-600">{invoice.created_at ? new Date(invoice.created_at).toLocaleDateString("fr-CA") : "—"}</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Échéance</p>
        <p className="text-slate-600">{invoice.due_date || "—"}</p>
      </div>
    </div>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
          <th className="py-2 font-medium">Description</th>
          <th className="py-2 font-medium text-right">Qté</th>
          <th className="py-2 font-medium text-right">Prix unit.</th>
          <th className="py-2 font-medium text-right">Montant</th>
        </tr>
      </thead>
      <tbody>
        {invoice.lines.map((l, i) => (
          <tr key={i} data-testid={`preview-line-${i}`} className="border-b border-slate-50">
            <td className="py-2.5 text-brand-navy">{l.description}</td>
            <td className="py-2.5 text-right text-slate-600">{l.quantity}</td>
            <td className="py-2.5 text-right text-slate-600">{Number(l.unit_price).toFixed(2)} $</td>
            <td className="py-2.5 text-right font-medium text-brand-navy">{Number(l.amount).toFixed(2)} $</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="flex justify-end mt-4">
      <div className="w-64 text-sm space-y-1.5">
        <div className="flex justify-between text-slate-600">
          <span>Sous-total</span><span data-testid="invoice-subtotal">{invoice.subtotal} $</span>
        </div>
        {invoice.apply_tps && (
          <div className="flex justify-between text-slate-600">
            <span>TPS (5 %)</span><span data-testid="invoice-tps">{invoice.tps} $</span>
          </div>
        )}
        {invoice.apply_tvq && (
          <div className="flex justify-between text-slate-600">
            <span>TVQ (9,975 %)</span><span data-testid="invoice-tvq">{invoice.tvq} $</span>
          </div>
        )}
        <div className="flex justify-between font-extrabold text-brand-navy text-base pt-2 border-t border-slate-200">
          <span>Total ({invoice.currency})</span><span data-testid="invoice-total">{invoice.total} $</span>
        </div>
      </div>
    </div>
    {invoice.notes && (
      <p data-testid="invoice-notes-preview" className="mt-6 text-xs text-slate-500 italic whitespace-pre-line">{invoice.notes}</p>
    )}
  </div>
);

const ReminderDialog = ({ draft, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Objet : ${draft.subject}\n\n${draft.body}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="reminder-dialog" className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-brand-navy flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand-turquoise" /> Brouillon de relance (IA SUGGEST)
          </h2>
          <button data-testid="reminder-close" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-brand-navy">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Brouillon généré par l'IA — relisez, ajustez et envoyez vous-même depuis votre messagerie. Aucun envoi automatique.
          </p>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Objet</p>
            <p data-testid="reminder-subject" className="text-sm font-medium text-brand-navy">{draft.subject}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Message</p>
            <p data-testid="reminder-body" className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{draft.body}</p>
          </div>
          <button
            data-testid="reminder-copy-button"
            onClick={copy}
            className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors"
          >
            {copied ? "Copié dans le presse-papiers" : "Copier le brouillon"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function InvoiceDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/business/invoices/${invoiceId}`);
      setInvoice(res.data);
    } catch (err) {
      setError(err?.response?.status || 500);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (user) {
      load();
      api.get("/business/contacts").then((r) => setContacts(r.data)).catch(() => {});
    }
  }, [user, load]);

  const setStatus = async (status) => {
    setActionError("");
    try {
      const res = await api.post(`/business/invoices/${invoice.invoice_id}/status`, { status });
      setInvoice(res.data);
    } catch (err) {
      setActionError(formatApiError(err));
    }
  };

  const remove = async () => {
    setActionError("");
    try {
      await api.delete(`/business/invoices/${invoice.invoice_id}`);
      navigate("/business?tab=factures");
    } catch (err) {
      setActionError(formatApiError(err));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${invoice.public_url}`);
      setLinkCopied(true);
    } catch {
      setLinkCopied(false);
    }
  };

  const suggestReminder = async () => {
    setActionError("");
    setReminderLoading(true);
    try {
      const res = await api.post(`/business/invoices/${invoice.invoice_id}/suggest-reminder`);
      setReminder(res.data.draft);
    } catch (err) {
      setActionError(formatApiError(err));
    } finally {
      setReminderLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div data-testid="invoice-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">
          Facture introuvable.
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="max-w-4xl mx-auto px-6 py-24 flex justify-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="invoice-detail-page" className="min-h-screen bg-slate-50">
      <div className="print:hidden"><PublicNav /></div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
          <button
            data-testid="invoice-back-button"
            onClick={() => navigate("/business?tab=factures")}
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-navy"
          >
            <ArrowLeft className="w-4 h-4" /> Mes factures
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="invoice-print-button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-100"
            >
              <Printer className="w-3.5 h-3.5" /> Imprimer
            </button>
            <a
              data-testid="invoice-pdf-link"
              href={`${API_BASE}/api/business/invoices/${invoice.invoice_id}/pdf`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-100"
            >
              <FileDown className="w-3.5 h-3.5" /> Télécharger le PDF
            </a>
            <button
              data-testid="invoice-copy-link-button"
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-100"
            >
              <Link2 className="w-3.5 h-3.5" /> {linkCopied ? "Lien copié !" : "Lien client (paiement)"}
            </button>
          </div>
        </div>

        {actionError && (
          <div data-testid="invoice-action-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 print:hidden">{actionError}</div>
        )}

        <InvoicePreview invoice={invoice} issuerName={user?.name || ""} />

        <div className="flex flex-wrap items-center gap-2 mt-6 print:hidden">
          {invoice.status === "brouillon" && (
            <>
              <button
                data-testid="invoice-edit-button"
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue border border-slate-300 rounded-lg px-4 py-2.5 hover:bg-slate-100"
              >
                Modifier
              </button>
              <button
                data-testid="invoice-send-button"
                onClick={() => setStatus("envoyee")}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-blue rounded-lg px-4 py-2.5 hover:bg-brand-navy"
              >
                <Send className="w-3.5 h-3.5" /> Marquer envoyée
              </button>
            </>
          )}
          {invoice.status === "envoyee" && (
            <>
              <button
                data-testid="invoice-mark-paid-button"
                onClick={() => setStatus("payee")}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-turquoise rounded-lg px-4 py-2.5 hover:bg-brand-navy"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Marquer payée
              </button>
              <button
                data-testid="invoice-reminder-button"
                onClick={suggestReminder}
                disabled={reminderLoading}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-orange border border-brand-orange/40 rounded-lg px-4 py-2.5 hover:bg-brand-orange/10 disabled:opacity-50"
              >
                <Sparkles className="w-3.5 h-3.5" /> {reminderLoading ? "L'IA rédige…" : "Relance IA"}
              </button>
            </>
          )}
          {(invoice.status === "brouillon" || invoice.status === "envoyee") && (
            <button
              data-testid="invoice-cancel-button"
              onClick={() => setStatus("annulee")}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-4 py-2.5 hover:bg-slate-100"
            >
              <Ban className="w-3.5 h-3.5" /> Annuler la facture
            </button>
          )}
          {invoice.status !== "payee" && (
            <button
              data-testid="invoice-delete-button"
              onClick={remove}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg px-4 py-2.5 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
          )}
        </div>
      </div>

      {editOpen && (
        <InvoiceFormDialog
          contacts={contacts}
          initial={invoice}
          onClose={() => setEditOpen(false)}
          onSaved={(inv) => { setEditOpen(false); setInvoice(inv); }}
        />
      )}
      {reminder && <ReminderDialog draft={reminder} onClose={() => setReminder(null)} />}
    </div>
  );
}
